/**
 * Email Allowlist Configuration (Frontend)
 * =============================================================================
 * Client-side validation for email allowlist
 *
 * IMPORTANT:
 * - This is for UX only - provides immediate feedback to users
 * - Real security enforcement happens in Firebase Cloud Functions
 * - Must stay in sync with backend/functions/src/allowlist.ts
 */

/**
 * Authorized email addresses
 * Case-insensitive matching is applied
 */
export const ALLOWED_EMAILS = [
  'ojasvi.rajpal@gmail.com',
  'adityatiwari210502@gmail.com',
  'hello.tomhwang@gmail.com',
  'jen.jjwang@gmail.com',
  'aadidev1018@gmail.com',
  'Kyle.Ekberg@bridgebio.com',
  'Emma.Lendt@bridgebio.com',
  'Dominic.Jud@bridgebio.com',
  'VanAnh.Hoang@bridgebio.com',
  'david.leung@bridgebio.com',
  'Rose.Gerdesmeier@bridgebio.com',
  'tom@precepi.com',
  'precepitest@outlook.com',
  // Geron
  'SRinguette@geron.com',
  'dmontgomery@geron.com',
  // Immunome
  'tkimhafken@immunome.com',
  'jheimfeld@immunome.com',
  'scaparelli@immunome.com',
  'alee01@immunome.com',
  'wgraves@immunome.com',
  'amurali@immunome.com',
  'rnguyen@immunome.com',
  'sramakrishnan@immunome.com',
  'ftian@immunome.com',
  // Pfizer
  'adetoun.awoyemi@pfizer.com',
  'melissa.m.wagenaar@pfizer.com',
  'samikshapramod.mirikar@pfizer.com',
  'trudi.c.morneau@pfizer.com',
  'karilyn.halloran@pfizer.com',
  'lori.a.lesko@pfizer.com',
  'Scott.B.Reed@pfizer.com',
  'Christos.Polizos@pfizer.com',
  'Jennifer.vandeRijn@pfizer.com',
  'oj@precepi.com',
  'eng@precepi.com',
];

/**
 * Admin email addresses — only these users see the Admin panel.
 * Must be a subset of ALLOWED_EMAILS.
 */
export const ADMIN_EMAILS = [
  'ojasvi.rajpal@gmail.com',
  'adityatiwari210502@gmail.com',
  'hello.tomhwang@gmail.com',
  'oj@precepi.com',
];

/**
 * Authorized email domains for NON-SSO providers only (Google/password/email-link).
 * ⚠️ Do NOT add SSO-enabled customer domains here — SSO domains are authorized by the
 * backend per-request binding (org_email_domains + organizations.sso_provider_id).
 * Adding one here violates defense-in-depth; the backend binding must still reject it.
 */
export const ALLOWED_DOMAINS: string[] = [];

/**
 * Check if an email is in the allowlist (individual emails or domain-level)
 * @param email - Email address to check (case-insensitive)
 * @returns true if email is allowed, false otherwise
 */
export function isEmailAllowed(email: string | undefined | null): boolean {
  if (!email) {
    return false;
  }

  const normalizedEmail = email.toLowerCase().trim();

  // Check individual email allowlist
  if (ALLOWED_EMAILS.some(
    allowedEmail => allowedEmail.toLowerCase() === normalizedEmail
  )) {
    return true;
  }

  // Check domain-level allowlist
  const domain = '@' + normalizedEmail.split('@')[1];
  if (ALLOWED_DOMAINS.some(
    allowedDomain => allowedDomain.toLowerCase() === domain
  )) {
    return true;
  }

  return false;
}

/**
 * Get user-friendly error message for unauthorized access
 */
export function getUnauthorizedMessage(email?: string): string {
  if (email) {
    return `The email address "${email}" is not authorized to access this application. Please contact an administrator if you believe this is an error.`;
  }
  return 'Your email is not authorized to access this application. Please contact an administrator.';
}
